import {
  FREE_TEXT,
  ALERT_TYPE,
  REPORT_ALERT_SEVERITY_MAP,
  ALERT_NOTIFICATION_METHODS,
} from "static/constants";
import { isValidArray } from "./array";

/**
 * Checks whether a notification config sends its alert by email.
 * @param {Object} config - A notify config with a notification_method.
 * @returns {boolean} True for Email configs.
 */
export const isEmailConfig = (config) =>
  config?.notification_method === ALERT_NOTIFICATION_METHODS.email;

/**
 * Builds the default email recipient selection for the alert notification modal:
 * every configured email recipient, keyed by the group's messageKey.
 * A group holds at most one Email config (configs are grouped by time period and message).
 * @param {Array} configGroups - Groups from generateNotificationConfigGroups.
 * @returns {Object} { [messageKey]: string[] } for each group that has an Email config.
 */
export const getDefaultSelectedRecipients = (configGroups = []) =>
  configGroups.reduce((acc, { messageKey, configs }) => {
    const emailConfig = configs?.find(isEmailConfig);
    if (emailConfig) acc[messageKey] = emailConfig.recipients || [];
    return acc;
  }, {});

/**
 * Determines the time period (regular or after hours) based on the provided time zone.
 * @param {string} timeZone - The time zone to consider (default is "EST").
 * @returns {string} The time period, either "regular" or "after_hours".
 */
export const getTimePeriod = (timeZone = "EST") => {
  const localHour = new Date().toLocaleString("en-US", {
    timeZone,
    hour: "2-digit",
    hour12: false,
  });

  return localHour >= 7 && localHour < 17
    ? "regular" // 7 AM - 5 PM
    : "after_hours"; // 5 PM - 7 AM
};

/**
 * Determines the maximum alert priority and corresponding alert type from a list of report notes.
 * @param {Array} reportNotes - An array of report note objects.
 * @returns {Object} An object containing the alert_type and alert_note.
 */
export const getMaximumAlertPriority = (reportNotes = []) => {
  const { alertType, criticalNotes, warningNotes } = reportNotes
    .flatMap(({ content = [] }) => content)
    .reduce(
      (acc, { alert, description }) => {
        const data = REPORT_ALERT_SEVERITY_MAP[alert];
        if (!data || !description) return acc;

        const { priority, color } = data;

        // Update max priority & alert type
        if (priority > acc.maxPriority) {
          acc.maxPriority = priority;
          acc.alertType = color;
        }

        if (alert === "Critical") acc.criticalNotes.push(description);
        else if (alert === "Warning") acc.warningNotes.push(description);

        return acc;
      },
      { maxPriority: 0, alertType: null, criticalNotes: [], warningNotes: [] }
    );

  const notes = [...criticalNotes, ...warningNotes];

  return {
    alert_type: alertType,
    alert_note: notes.length ? notes.join("; ") : null,
  };
};

export const formatMessagePayload = (alertInfo, reportDetail) => {
  const { alert_type, alert_note } = alertInfo;

  const {
    manufacturer,
    device_category,
    patient: { name, dob, mrn, physician } = {},
  } = reportDetail || {};

  const data = {
    Name: name,
    DOB: dob,
    MRN: mrn,
    Manufacturer: manufacturer,
    "Device Type": device_category,
    "Alert Type": alert_type,
    "Alert Description": alert_note,
    "Following Physician": physician,
  };

  return data;
};

/**
 * Generates a message content string based on the provided message contents and data.
 * @param {Array} messageContents - An array of message content objects containing title and free_text.
 * @param {Object} alertInfo - An object containing alert_type and alert_note.
 * @param {Object} reportInfo - An object containing report details including patient and device information.
 * @returns {string} The generated message content string.
 */
export const generateMessageContent = (
  messageContents,
  alertInfo,
  reportInfo
) => {
  const messagePayload = formatMessagePayload(alertInfo, reportInfo);

  return messageContents
    ?.map(({ title, free_text }) => {
      let content = "";

      if (title === FREE_TEXT) {
        content = free_text;
      } else if (title === "Alert Type") {
        content = `${messagePayload?.[title]} alert`;
      } else {
        content = messagePayload?.[title];
      }

      return content ?? "";
    })
    ?.filter((item) => item.trim())
    ?.join(", ");
};

/**
 * Determines the active alert notification configurations based on the maximum alert priority, account alert configurations, and time zone.
 * @param {Object} maximumAlertPriority - The maximum alert priority object containing alert_type and alert_note.
 * @param {Array} alertConfigurations - An array of alert configuration objects for the account.
 * @param {string} timeZone - The time zone to consider for determining the active configurations.
 * @returns {Array} An array of active alert notification configurations.
 */
export const getActiveAlertConfigurations = (
  maximumAlertPriority,
  alertConfigurations,
  timeZone = "EST"
) => {
  const { alert_type, alert_note } = maximumAlertPriority || {};

  if (
    !alert_type ||
    !alert_note ||
    !Object.keys(ALERT_TYPE).includes(alert_type)
  ) {
    return [];
  }

  const configuration = alertConfigurations?.find(
    (config) => config.alert_type === alert_type
  );

  if (!configuration) return [];

  const timePeriod = getTimePeriod(timeZone);

  // Get all active configs for the current time period
  const activeNotifyConfigs =
    configuration.notify_configs?.filter(
      (config) => config.time_period === timePeriod && config.active
    ) || [];

  return activeNotifyConfigs;
};

export const generateMessageKey = (config) => {
  const { time_period, message_content = [] } = config;

  const normalizedMessageContent = [...message_content]
    .map(({ title, free_text }) => ({
      title: (title ?? "").trim(),
      free_text: (free_text ?? "").trim(),
    }))
    .sort((a, b) => {
      const titleCompare = (a.title || "").localeCompare(b.title || "");

      if (titleCompare !== 0) {
        return titleCompare;
      }

      return (a.free_text || "").localeCompare(b.free_text || "");
    });

  const messageContentKey = normalizedMessageContent
    .map(({ title, free_text }) => `${title}_${free_text}`)
    .join("|");

  return `${time_period}::${messageContentKey}`;
};

/**
 * Generates grouped notification configurations based on the provided notification configurations, alert information, and report details.
 * @param {Array|Object} notificationConfigs - The notification configuration(s) to group.
 * @param {Object} alertInfo - An object containing alert information for generating message content.
 * @param {Object} reportInfo - An object containing report details for generating message content.
 * @returns {Array} An array of grouped notification configurations.
 */
export const generateNotificationConfigGroups = (
  notificationConfigs,
  alertInfo,
  reportInfo
) => {
  let configs = [];
  if (isValidArray(notificationConfigs)) {
    configs = notificationConfigs;
  } else if (notificationConfigs) {
    configs = [notificationConfigs];
  }

  const { groupsMap, messageContents } = configs.reduce(
    (acc, config) => {
      const messageKey = generateMessageKey(config);

      if (!acc.groupsMap[messageKey]) {
        const messageContent = generateMessageContent(
          config?.message_content,
          alertInfo,
          reportInfo
        );

        acc.groupsMap[messageKey] = {
          messageKey,
          messageContent,
          configs: [],
        };

        acc.messageContents[messageKey] = messageContent;
      }

      acc.groupsMap[messageKey].configs.push(config);

      return acc;
    },
    { groupsMap: {}, messageContents: {} }
  );

  return {
    configGroups: Object.values(groupsMap),
    messageContents,
  };
};
